import React, { useMemo } from 'react';

import { ApiObject, Program, School } from 'app/types';
import Text from 'common/components/Text/Text';

import styles from 'features/explore/school/SchoolDetail.module.scss';
import { useMediaQuery } from 'react-responsive';
import { buildStyles, CircularProgressbarWithChildren } from 'react-circular-progressbar';
import { programLabelDetail, entityIsSchool, entityIsProgram } from 'common/utils';
import { ShineIcon, UnderlineShine, NotificationIcon, DancingPeople } from 'assets/explore';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'app/hooks';
import { setFilters } from 'features/session/sessionSlice';

interface CompProps {
  entity: ApiObject<School> | ApiObject<Program> | undefined;
  comesFromExplore?: boolean;
  handleApply?: () => void;
}

export const MatchingSection = ({
  entity,
  comesFromExplore,
  handleApply,
}: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const preferencesLabel = useMemo(
    () =>
      entityIsSchool(entity?.data)
        ? programLabelDetail(
            entity?.data.best_preferences_score
              ? entity.data.best_preferences_score * 100
              : undefined
          )
        : entityIsProgram(entity?.data)
        ? programLabelDetail(entity?.data.preferences_score)
        : undefined,
    [entity]
  );

  const preferencesScore = useMemo(
    () =>
      entityIsSchool(entity?.data)
        ? entity?.data.best_preferences_score
          ? entity.data.best_preferences_score * 100
          : 0
        : entityIsProgram(entity?.data)
        ? entity?.data.preferences_score
          ? entity.data.preferences_score
          : 0
        : 0,
    [entity]
  );
  const preferencesMessage = useMemo(
    () =>
      entityIsSchool(entity?.data)
        ? preferencesLabel
          ? entity?.data.best_programs_amount && entity?.data.best_programs_amount > 0
            ? ` ${entity?.data.best_programs_amount} programs in this university match your profile`
            : "Don't worry! Unify can help you identify your best match universities!"
          : 'UnifAI digital counselor helps you match with the perfect program for you!'
        : entityIsProgram(entity?.data)
        ? preferencesLabel
          ? preferencesLabel === 'High'
            ? 'This program perfectly matches your profile. Apply Now!'
            : "Don't worry! You still can apply to this program!"
          : 'UnifAI digital counselor helps you match with the perfect program for you!'
        : '',
    [entity]
  );

  const handleSchoolFilter = (school_name?: string, school_id?: number) => {
    school_name &&
      school_id &&
      dispatch(
        setFilters({ query: school_name, order_by_scoring: 'desc', school_search: school_id })
      );
    navigate('/explore/programs');
  };

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.matchingSection}>
        <img src={DancingPeople} className={styles.dancingPeople}></img>
        <div className={styles.graphicsContainer}>
          <img src={NotificationIcon} className={styles.notificationIcon}></img>
          <div className={styles.graphCol}>
            <div className={`${styles.graphic} ${!preferencesLabel ? styles.blurred : ''}`}>
              <CircularProgressbarWithChildren
                value={preferencesScore}
                strokeWidth={6}
                styles={buildStyles({
                  pathColor: '#A1E4A1',
                  trailColor: '#A6C6F9',
                })}
              >
                <div className={styles.graphText}>
                  {preferencesLabel ? `${preferencesLabel}` : 'High'}
                </div>
              </CircularProgressbarWithChildren>
            </div>
            <div>
              <Text
                size={12}
                inlineStyle={{
                  textAlign: 'center',
                  color: '#193676',
                  marginTop: '8px',
                  maxWidth: '80px',
                }}
              >
                Preference match
              </Text>
            </div>
          </div>
        </div>
        <div className={styles.matchingInformation}>
          <Text
            size={isMobile ? 14 : comesFromExplore ? 18 : 20}
            weight={600}
            inlineStyle={
              isMobile
                ? {
                    textAlign: 'center',
                    color: '#19234F',
                    position: 'relative',
                  }
                : { color: '#19234F', position: 'relative', maxWidth: 'inherit' }
            }
          >
            <img src={ShineIcon} className={styles.shineIcon}></img>
            {preferencesMessage}
          </Text>
          <img src={UnderlineShine} className={styles.underlineIcon}></img>
          {preferencesLabel ? (
            <button
              className={`btn btn-primary btn-lg ${styles.matchingButton}`}
              onClick={() =>
                entityIsSchool(entity?.data)
                  ? handleSchoolFilter(entity?.data.name, entity?.data.id)
                  : handleApply
                  ? handleApply()
                  : ''
              }
            >
              {entityIsSchool(entity?.data) ? 'View programs' : 'Apply Now'}
            </button>
          ) : (
            <button
              className={`btn btn-primary btn-lg ${styles.matchingButton}`}
              onClick={() => navigate('/profile')}
            >
              Fill UnifAI
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
